import React, { useState, useEffect, useRef } from 'react';
import clientCandidateDataService from '../../../services/clientCandidateDataService';

/**
 * CompensationWidget
 * Contains: Total Compensation Target (calculated), Current Base, Current Total Comp,
 * Expected Base Range, Expected Total, and Compensation History
 */

const CompensationWidget = ({ profile, candidateId, onRefresh }) => {
  const [editingField, setEditingField] = useState(null);
  const [isAddingHistory, setIsAddingHistory] = useState(false);
  const [editingHistoryIndex, setEditingHistoryIndex] = useState(null);
  const [historyForm, setHistoryForm] = useState({
    date: new Date().toISOString().split('T')[0],
    current_base_salary: '',
    current_total_min: '',
    current_total_max: '',
    expected_base_min: '',
    expected_base_max: '',
    expected_total_min: '',
    expected_total_max: '',
    notes: ''
  });

  const clientData = profile?.client_data || {};

  // Optimistic local overrides — when a field saves, we update this immediately
  // so the display reflects the new value without waiting for a parent reload.
  // Falls through to clientData when no override exists for that field.
  const [localOverrides, setLocalOverrides] = useState({});
  const getVal = (field) => (
    Object.prototype.hasOwnProperty.call(localOverrides, field)
      ? localOverrides[field]
      : (clientData[field] || '')
  );

  const compensation = {
    current_base_salary: getVal('current_base_salary'),
    current_total_comp: getVal('current_total_comp'),
    current_total_min: getVal('current_total_min'),
    current_total_max: getVal('current_total_max'),
    expected_base_min: getVal('expected_base_min'),
    expected_base_max: getVal('expected_base_max'),
    expected_total: getVal('expected_total'),
    expected_total_min: getVal('expected_total_min'),
    expected_total_max: getVal('expected_total_max')
  };
  const compensationHistory = clientData.compensation_history || [];

  const calculateTotalCompTarget = (baseMin, baseMax, expectedTotal, totalMin, totalMax) => {
    const fmt = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : n >= 1000 ? `$${(n/1000).toFixed(0)}K` : `$${n.toLocaleString()}`;
    const tMin = parseFloat(totalMin) || 0;
    const tMax = parseFloat(totalMax) || 0;
    const total = parseFloat(expectedTotal) || 0;
    const min = parseFloat(baseMin) || 0;
    const max = parseFloat(baseMax) || 0;

    // Prefer the explicit range
    if (tMin > 0 && tMax > 0) return tMin === tMax ? fmt(tMin) : `${fmt(tMin)} – ${fmt(tMax)}`;
    if (tMin > 0) return `${fmt(tMin)}+`;
    if (tMax > 0) return `Up to ${fmt(tMax)}`;
    // Fallback to legacy single value
    if (total > 0) return fmt(total);
    // Otherwise fall back to base range
    if (min === 0 && max === 0) return null;
    return min === max || max === 0 ? fmt(min) : `${fmt(min)} – ${fmt(max)}`;
  };

  const currentTotalCompTarget = calculateTotalCompTarget(
    compensation.expected_base_min,
    compensation.expected_base_max,
    compensation.expected_total,
    compensation.expected_total_min,
    compensation.expected_total_max
  );

  const saveData = async (updates, opts = {}) => {
    // Optimistic local update so the UI reflects the change immediately
    // (covers the "empty -> first value" case where the parent's clientData
    // hasn't refreshed yet).
    setLocalOverrides(prev => ({ ...prev, ...updates }));
    try {
      // Clean status is no longer reset here — see PreferencesWidget note.
      // Marking a profile unclean is reserved for tag changes only.
      await clientCandidateDataService.updateData(candidateId, { ...clientData, ...updates });
      // Skip onRefresh for field-level updates so debounced auto-saves don't
      // trigger a full reload on every keystroke. History add/edit/delete still
      // refresh so the list re-renders from the updated clientData.
      if (!opts.silent && onRefresh) onRefresh();
    } catch (error) {
      // Roll back the optimistic update on failure
      setLocalOverrides(prev => {
        const next = { ...prev };
        Object.keys(updates).forEach(k => { delete next[k]; });
        return next;
      });
      console.error('Error saving:', error);
      alert('Failed to save');
    }
  };

  const handleSaveField = async (field, value) => {
    await saveData({ [field]: value }, { silent: true });
    // Note: caller (CompField) controls editing state; we don't reset it here.
  };

  // History handlers
  const handleAddHistory = () => {
    setHistoryForm({
      date: new Date().toISOString().split('T')[0],
      current_base_salary: '',
      current_total_min: '',
      current_total_max: '',
      expected_base_min: '',
      expected_base_max: '',
      expected_total_min: '',
      expected_total_max: '',
      notes: ''
    });
    setIsAddingHistory(true);
    setEditingHistoryIndex(null);
  };

  const handleEditHistory = (index) => {
    const record = compensationHistory[index];
    setHistoryForm({
      date: record.date || new Date().toISOString().split('T')[0],
      current_base_salary: record.current_base_salary || '',
      current_total_min: record.current_total_min || record.current_total_comp || '',
      current_total_max: record.current_total_max || '',
      expected_base_min: record.expected_base_min || '',
      expected_base_max: record.expected_base_max || '',
      expected_total_min: record.expected_total_min || record.expected_total || record.bonus_expectations || '',
      expected_total_max: record.expected_total_max || '',
      notes: record.notes || ''
    });
    setEditingHistoryIndex(index);
    setIsAddingHistory(false);
  };

  const handleSaveHistory = async () => {
    const anyValue = ['current_base_salary','current_total_min','current_total_max','expected_base_min','expected_base_max','expected_total_min','expected_total_max']
      .some(k => historyForm[k] && String(historyForm[k]).trim() !== '');
    if (!anyValue) {
      alert('Please enter at least one compensation value');
      return;
    }

    const newRecord = {
      ...historyForm,
      created_at: editingHistoryIndex !== null 
        ? compensationHistory[editingHistoryIndex].created_at 
        : new Date().toISOString()
    };

    let newHistory = [...compensationHistory];
    if (editingHistoryIndex !== null) {
      newHistory[editingHistoryIndex] = newRecord;
    } else {
      newHistory.unshift(newRecord); // Add to beginning
    }

    // Sort by date descending
    newHistory.sort((a, b) => new Date(b.date) - new Date(a.date));

    await saveData({ compensation_history: newHistory });
    setIsAddingHistory(false);
    setEditingHistoryIndex(null);
  };

  const handleDeleteHistory = async (index) => {
    if (!window.confirm('Delete this compensation record?')) return;
    const newHistory = compensationHistory.filter((_, i) => i !== index);
    await saveData({ compensation_history: newHistory });
  };

  const handleCancelHistory = () => {
    setIsAddingHistory(false);
    setEditingHistoryIndex(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return `$${Number(value).toLocaleString()}`;
  };

  return (
    <div className="space-y-4" data-widget="compensation">
      {/* Total Compensation Target - At the top */}
      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
        <label className="block text-xs font-medium text-green-800 mb-1">Total Compensation Target</label>
        <p className="text-lg font-bold text-green-700">
          {currentTotalCompTarget || <span className="text-gray-400 font-normal text-sm">Enter expected base and total to calculate</span>}
        </p>
        <p className="text-xs text-green-600 mt-1">Expected Total Compensation</p>
      </div>

      <hr className="border-gray-200" />

      {/* Current Compensation */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-2">Current Compensation</label>
        <CompField 
          label="Current Base Salary" 
          field="current_base_salary" 
          value={compensation.current_base_salary} 
          editingField={editingField} 
          setEditingField={setEditingField} 
          onSave={handleSaveField} 
        />
        <div className="grid grid-cols-2 gap-2">
          <CompField
            label="Current Total (Min)"
            field="current_total_min"
            value={compensation.current_total_min}
            editingField={editingField}
            setEditingField={setEditingField}
            onSave={handleSaveField}
          />
          <CompField
            label="Current Total (Max)"
            field="current_total_max"
            value={compensation.current_total_max}
            editingField={editingField}
            setEditingField={setEditingField}
            onSave={handleSaveField}
          />
        </div>
      </div>

      <hr className="border-gray-200" />

      {/* Expected Compensation */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-2">Expected Compensation</label>
        <div className="grid grid-cols-2 gap-2">
          <CompField 
            label="Expected Base (Min)" 
            field="expected_base_min" 
            value={compensation.expected_base_min} 
            editingField={editingField} 
            setEditingField={setEditingField} 
            onSave={handleSaveField} 
          />
          <CompField 
            label="Expected Base (Max)" 
            field="expected_base_max" 
            value={compensation.expected_base_max} 
            editingField={editingField} 
            setEditingField={setEditingField} 
            onSave={handleSaveField} 
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <CompField
            label="Expected Total (Min)"
            field="expected_total_min"
            value={compensation.expected_total_min}
            editingField={editingField}
            setEditingField={setEditingField}
            onSave={handleSaveField}
          />
          <CompField
            label="Expected Total (Max)"
            field="expected_total_max"
            value={compensation.expected_total_max}
            editingField={editingField}
            setEditingField={setEditingField}
            onSave={handleSaveField}
          />
        </div>
      </div>

      <hr className="border-gray-200" />

      {/* Compensation History */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-600">Compensation History</label>
          {!isAddingHistory && editingHistoryIndex === null && (
            <button 
              onClick={handleAddHistory}
              className="text-xs text-emerald-700 hover:text-emerald-700 flex items-center gap-1"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Record
            </button>
          )}
        </div>

        {/* Add/Edit History Form */}
        {(isAddingHistory || editingHistoryIndex !== null) && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3">
            <h4 className="font-medium text-sm text-emerald-900 mb-2">
              {editingHistoryIndex !== null ? 'Edit' : 'Add'} Compensation Record
            </h4>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Date *</label>
                <input
                  type="date"
                  value={historyForm.date}
                  onChange={(e) => setHistoryForm({ ...historyForm, date: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border rounded"
                />
              </div>

              {/* Current Compensation */}
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">Current</label>
                <div className="space-y-1.5">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Current Base Salary</label>
                    <input
                      type="text" inputMode="numeric"
                      value={historyForm.current_base_salary}
                      onChange={(e) => setHistoryForm({ ...historyForm, current_base_salary: e.target.value.replace(/[^0-9]/g, '') })}
                      className="w-full px-2 py-1.5 text-sm border rounded"
                      placeholder="120000"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Current Total (Min)</label>
                      <input
                        type="text" inputMode="numeric"
                        value={historyForm.current_total_min}
                        onChange={(e) => setHistoryForm({ ...historyForm, current_total_min: e.target.value.replace(/[^0-9]/g, '') })}
                        className="w-full px-2 py-1.5 text-sm border rounded"
                        placeholder="150000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Current Total (Max)</label>
                      <input
                        type="text" inputMode="numeric"
                        value={historyForm.current_total_max}
                        onChange={(e) => setHistoryForm({ ...historyForm, current_total_max: e.target.value.replace(/[^0-9]/g, '') })}
                        className="w-full px-2 py-1.5 text-sm border rounded"
                        placeholder="200000"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Expected Compensation */}
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">Expected</label>
                <div className="space-y-1.5">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Expected Base (Min)</label>
                      <input
                        type="text" inputMode="numeric"
                        value={historyForm.expected_base_min}
                        onChange={(e) => setHistoryForm({ ...historyForm, expected_base_min: e.target.value.replace(/[^0-9]/g, '') })}
                        className="w-full px-2 py-1.5 text-sm border rounded"
                        placeholder="150000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Expected Base (Max)</label>
                      <input
                        type="text" inputMode="numeric"
                        value={historyForm.expected_base_max}
                        onChange={(e) => setHistoryForm({ ...historyForm, expected_base_max: e.target.value.replace(/[^0-9]/g, '') })}
                        className="w-full px-2 py-1.5 text-sm border rounded"
                        placeholder="175000"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Expected Total (Min)</label>
                      <input
                        type="text" inputMode="numeric"
                        value={historyForm.expected_total_min}
                        onChange={(e) => setHistoryForm({ ...historyForm, expected_total_min: e.target.value.replace(/[^0-9]/g, '') })}
                        className="w-full px-2 py-1.5 text-sm border rounded"
                        placeholder="200000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Expected Total (Max)</label>
                      <input
                        type="text" inputMode="numeric"
                        value={historyForm.expected_total_max}
                        onChange={(e) => setHistoryForm({ ...historyForm, expected_total_max: e.target.value.replace(/[^0-9]/g, '') })}
                        className="w-full px-2 py-1.5 text-sm border rounded"
                        placeholder="250000"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">Notes (optional)</label>
                <input 
                  type="text" 
                  value={historyForm.notes}
                  onChange={(e) => setHistoryForm({ ...historyForm, notes: e.target.value })}
                  className="w-full px-2 py-1 text-sm border rounded"
                  placeholder="e.g., Initial discussion, Counter offer, etc."
                />
              </div>
            </div>

            <div className="flex gap-2 mt-3">
              <button 
                onClick={handleSaveHistory}
                className="px-3 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700"
              >
                {editingHistoryIndex !== null ? 'Update' : 'Add'}
              </button>
              <button 
                onClick={handleCancelHistory}
                className="px-3 py-1 border text-xs rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* History List */}
        {compensationHistory.length === 0 && !isAddingHistory ? (
          <p className="text-sm text-gray-400 italic">No compensation history recorded</p>
        ) : (
          <div className="space-y-2">
            {compensationHistory.map((record, index) => {
              const totalTarget = calculateTotalCompTarget(
                record.expected_base_min,
                record.expected_base_max,
                record.expected_total || record.bonus_expectations,
                record.expected_total_min,
                record.expected_total_max
              );

              // Format helpers for inline display
              const fmtRange = (min, max) => {
                const hasMin = min !== null && min !== undefined && min !== '';
                const hasMax = max !== null && max !== undefined && max !== '';
                if (hasMin && hasMax) return min === max ? formatCurrency(min) : `${formatCurrency(min)} – ${formatCurrency(max)}`;
                if (hasMin) return `${formatCurrency(min)}+`;
                if (hasMax) return `Up to ${formatCurrency(max)}`;
                return null;
              };
              const expectedTotalDisplay = fmtRange(record.expected_total_min, record.expected_total_max)
                || (record.expected_total ? formatCurrency(record.expected_total) : null)
                || (record.bonus_expectations ? formatCurrency(record.bonus_expectations) : null);
              const currentTotalDisplay = fmtRange(record.current_total_min, record.current_total_max)
                || (record.current_total_comp ? formatCurrency(record.current_total_comp) : null);
              const expectedBaseDisplay = fmtRange(record.expected_base_min, record.expected_base_max);

              return (
                <div key={index} className="bg-gray-50 rounded-lg p-2 group">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-gray-500">
                          {formatDate(record.date)}
                        </span>
                        {record.auto && (
                          <span className="text-[9px] uppercase tracking-wider bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-medium">
                            auto
                          </span>
                        )}
                        {totalTarget && (
                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                            Target: {totalTarget}
                          </span>
                        )}
                      </div>
                      
                      <div className="text-xs text-gray-600 space-y-0.5">
                        <div className="flex gap-4 flex-wrap">
                          {record.current_base_salary && <span>Current Base: {formatCurrency(record.current_base_salary)}</span>}
                          {currentTotalDisplay && <span>Current Total: {currentTotalDisplay}</span>}
                          {expectedBaseDisplay && <span>Expected Base: {expectedBaseDisplay}</span>}
                          {expectedTotalDisplay && <span>Expected Total: {expectedTotalDisplay}</span>}
                        </div>
                        {record.notes && (
                          <p className="text-gray-500 italic">{record.notes}</p>
                        )}
                      </div>
                    </div>

                    {/* Edit/Delete buttons */}
                    <div className="opacity-0 group-hover:opacity-100 flex gap-1 ml-2">
                      <button 
                        onClick={() => handleEditHistory(index)}
                        className="p-1 text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 rounded"
                        title="Edit"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => handleDeleteHistory(index)}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const CompField = ({ label, field, value, editingField, setEditingField, onSave }) => {
  const [tempValue, setTempValue] = useState(value);
  const debounceRef = useRef(null);
  useEffect(() => { setTempValue(value); }, [value]);

  // Debounced auto-save on every keystroke. 500ms after the user stops typing,
  // the value persists. No commit buttons needed.
  const scheduleSave = (newValue) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSave(field, newValue);
    }, 500);
  };

  if (editingField === field) {
    return (
      <div className="mb-2">
        <label className="block text-xs text-gray-600 mb-1">{label}</label>
        <input
          type="text"
          inputMode="numeric"
          value={tempValue}
          onChange={(e) => { setTempValue(e.target.value); scheduleSave(e.target.value); }}
          onBlur={() => {
            if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; onSave(field, tempValue); }
            setEditingField(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; onSave(field, tempValue); }
              setEditingField(null);
            } else if (e.key === 'Escape') {
              if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
              setTempValue(value);
              setEditingField(null);
            }
          }}
          autoFocus
          placeholder="Enter amount"
          className="w-full px-2 py-1.5 text-sm bg-gray-50 border border-emerald-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
      </div>
    );
  }

  return (
    <div className="mb-2 group">
      <label className="block text-xs text-gray-600 mb-1">{label}</label>
      <div
        className="flex items-center justify-between px-2 py-1.5 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => setEditingField(field)}
      >
        <span className="text-sm text-gray-800">
          {value ? `$${Number(value).toLocaleString()}` : <span className="text-gray-400">Click to add</span>}
        </span>
        <svg className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </div>
    </div>
  );
};

export default CompensationWidget;
